import { useForm } from '@inertiajs/react'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import AdminLayout from '@/layouts/AdminLayout'

export default function OrganizationsNew() {
  const { data, setData, post, processing, errors } = useForm({
    name: '',
    slug: '',
    internal_domain: '',
    imap_host: '',
    imap_port: '993',
    imap_user: '',
    imap_password: '',
    imap_mailbox: 'INBOX',
    imap_ssl: true,
    outbound_email_provider: 'postmark',
    smtp_host: '',
    smtp_port: '587',
    smtp_user: '',
    smtp_password: '',
    outbound_from_name: 'XPORT Operations',
    smtp_from_address: 'ops@getnereus.ai',
    outbound_reply_to_address: '',
    smtp_auth: 'plain',
    smtp_starttls: true,
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post('/admin/organizations')
  }

  function handleNameChange(value: string) {
    setData((prev) => ({
      ...prev,
      name: value,
      slug: value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
    }))
  }

  return (
    <>
      <header className="flex h-14 items-center justify-between border-b px-8">
        <div className="flex items-center gap-3">
          <button
            className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent cursor-pointer"
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <h1 className="text-sm font-semibold">New Organization</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => window.history.back()}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="org-form"
            size="sm"
            className="text-xs"
            disabled={processing}
          >
            Create Organization
          </Button>
        </div>
      </header>

      <div className="max-w-2xl p-8">
        <form id="org-form" onSubmit={handleSubmit} className="space-y-8">
          {/* General */}
          <section>
            <h2 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              General
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="text-xs">Name</Label>
                  <Input
                    id="name"
                    placeholder="Acme Logistics"
                    value={data.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    className="mt-1 h-9 text-[13px]"
                  />
                  {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
                </div>
                <div>
                  <Label htmlFor="slug" className="text-xs">Slug</Label>
                  <Input
                    id="slug"
                    placeholder="acme-logistics"
                    value={data.slug}
                    onChange={(e) => setData('slug', e.target.value)}
                    className="mt-1 h-9 text-[13px] font-mono"
                  />
                  {errors.slug && <p className="mt-1 text-xs text-red-500">{errors.slug}</p>}
                </div>
              </div>
              <div>
                <Label htmlFor="internal_domain" className="text-xs">Internal Domain</Label>
                <Input
                  id="internal_domain"
                  placeholder="acme.com"
                  value={data.internal_domain}
                  onChange={(e) => setData('internal_domain', e.target.value)}
                  className="mt-1 h-9 text-[13px]"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Emails from this domain are treated as internal (sent by staff).
                </p>
              </div>
            </div>
          </section>

          {/* IMAP Settings */}
          <section>
            <h2 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              IMAP Settings
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="imap_host" className="text-xs">Host</Label>
                  <Input
                    id="imap_host"
                    placeholder="imap.gmail.com"
                    value={data.imap_host}
                    onChange={(e) => setData('imap_host', e.target.value)}
                    className="mt-1 h-9 text-[13px]"
                  />
                </div>
                <div>
                  <Label htmlFor="imap_port" className="text-xs">Port</Label>
                  <Input
                    id="imap_port"
                    type="number"
                    placeholder="993"
                    value={data.imap_port}
                    onChange={(e) => setData('imap_port', e.target.value)}
                    className="mt-1 h-9 text-[13px]"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="imap_ssl"
                  checked={data.imap_ssl}
                  onChange={(e) => setData('imap_ssl', e.target.checked)}
                  className="cursor-pointer"
                />
                <Label htmlFor="imap_ssl" className="text-xs cursor-pointer">Use SSL/TLS</Label>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="imap_user" className="text-xs">User</Label>
                  <Input
                    id="imap_user"
                    placeholder="ops@acme.com"
                    value={data.imap_user}
                    onChange={(e) => setData('imap_user', e.target.value)}
                    className="mt-1 h-9 text-[13px]"
                  />
                </div>
                <div>
                  <Label htmlFor="imap_password" className="text-xs">Password</Label>
                  <Input
                    id="imap_password"
                    type="password"
                    placeholder="App password"
                    value={data.imap_password}
                    onChange={(e) => setData('imap_password', e.target.value)}
                    className="mt-1 h-9 text-[13px]"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="imap_mailbox" className="text-xs">Mailbox</Label>
                <Input
                  id="imap_mailbox"
                  placeholder="INBOX"
                  value={data.imap_mailbox}
                  onChange={(e) => setData('imap_mailbox', e.target.value)}
                  className="mt-1 h-9 text-[13px]"
                />
              </div>
            </div>
          </section>

          {/* Outbound Email */}
          <section>
            <h2 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Outbound Email
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="outbound_email_provider" className="text-xs">Delivery Provider</Label>
                  <select
                    id="outbound_email_provider"
                    value={data.outbound_email_provider}
                    onChange={(e) => setData('outbound_email_provider', e.target.value)}
                    className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-[13px] shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring cursor-pointer"
                  >
                    <option value="postmark">Postmark</option>
                    <option value="smtp">Custom SMTP</option>
                  </select>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Postmark uses the server token configured in the environment.
                  </p>
                </div>
              </div>
              {data.outbound_email_provider === 'smtp' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="smtp_host" className="text-xs">Host</Label>
                      <Input
                        id="smtp_host"
                        placeholder="smtp.gmail.com"
                        value={data.smtp_host}
                        onChange={(e) => setData('smtp_host', e.target.value)}
                        className="mt-1 h-9 text-[13px]"
                      />
                    </div>
                    <div>
                      <Label htmlFor="smtp_port" className="text-xs">Port</Label>
                      <Input
                        id="smtp_port"
                        type="number"
                        placeholder="587"
                        value={data.smtp_port}
                        onChange={(e) => setData('smtp_port', e.target.value)}
                        className="mt-1 h-9 text-[13px]"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="smtp_user" className="text-xs">User</Label>
                      <Input
                        id="smtp_user"
                        placeholder="ops@acme.com"
                        value={data.smtp_user}
                        onChange={(e) => setData('smtp_user', e.target.value)}
                        className="mt-1 h-9 text-[13px]"
                      />
                    </div>
                    <div>
                      <Label htmlFor="smtp_password" className="text-xs">Password</Label>
                      <Input
                        id="smtp_password"
                        type="password"
                        placeholder="App password"
                        value={data.smtp_password}
                        onChange={(e) => setData('smtp_password', e.target.value)}
                        className="mt-1 h-9 text-[13px]"
                      />
                    </div>
                  </div>
                </>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="outbound_from_name" className="text-xs">From Name</Label>
                  <Input
                    id="outbound_from_name"
                    type="text"
                    placeholder="XPORT Operations"
                    value={data.outbound_from_name}
                    onChange={(e) => setData('outbound_from_name', e.target.value)}
                    className="mt-1 h-9 text-[13px]"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Displayed next to the sender address.
                  </p>
                </div>
                <div>
                  <Label htmlFor="smtp_from_address" className="text-xs">From Address</Label>
                  <Input
                    id="smtp_from_address"
                    type="text"
                    placeholder="ops@acme.com"
                    value={data.smtp_from_address}
                    onChange={(e) => setData('smtp_from_address', e.target.value)}
                    className="mt-1 h-9 text-[13px]"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Must be verified with the outbound provider.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="outbound_reply_to_address" className="text-xs">Reply-To Address</Label>
                  <Input
                    id="outbound_reply_to_address"
                    type="text"
                    placeholder="ops@acme.com"
                    value={data.outbound_reply_to_address}
                    onChange={(e) => setData('outbound_reply_to_address', e.target.value)}
                    className="mt-1 h-9 text-[13px]"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Leave blank to use the IMAP user address.
                  </p>
                </div>
              </div>
              {data.outbound_email_provider === 'smtp' && (
                <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="smtp_auth" className="text-xs">Auth Method</Label>
                  <select
                    id="smtp_auth"
                    value={data.smtp_auth}
                    onChange={(e) => setData('smtp_auth', e.target.value)}
                    className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-[13px] shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring cursor-pointer"
                  >
                    <option value="plain">Plain</option>
                    <option value="login">Login</option>
                    <option value="cram_md5">CRAM-MD5</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="smtp_starttls"
                  checked={data.smtp_starttls as boolean}
                  onCheckedChange={(checked) => setData('smtp_starttls', checked === true)}
                />
                <Label htmlFor="smtp_starttls" className="text-xs cursor-pointer">Enable STARTTLS</Label>
              </div>
                </>
              )}
            </div>
          </section>
        </form>
      </div>
    </>
  )
}

OrganizationsNew.layout = (page: React.ReactNode) => (
  <AdminLayout>{page}</AdminLayout>
)
